import { randomUUID } from 'node:crypto';
import { createApiClient } from '../../core/api-client.js';
import { requireProjectId } from '../../core/auth.js';
import { apiFetch } from '../../core/http.js';
import {
  ApiError,
  handleCliError,
  throwApiError,
  throwApiResponseError,
} from '../../core/api-error.js';
import { parseSSEEvents } from '../../core/sse.js';
import { createCLILogger } from '../../core/cli-logger.js';
import { writeResult } from '../../core/output.js';
import type { GlobalOptions } from '../../types/global.js';
import type { components } from '../../types/api.gen.js';
import { getFlow } from '../flows/index.js';

/**
 * Response body of POST .../settings/{settingsId}/deploy, now that the
 * per-settings deploy route is in the OpenAPI contract (api.gen.d.ts).
 */
type DeploySettingsResponse = components['schemas']['DeploySettingsResponse'];

// === Helpers ===

async function resolveSettingsId(options: {
  flowId: string;
  projectId: string;
  flowName: string;
}): Promise<string> {
  const flow = await getFlow({
    flowId: options.flowId,
    projectId: options.projectId,
  });
  const settings = (flow as { settings?: Array<{ id: string; name: string }> })
    .settings;
  if (!settings?.length) {
    throw new Error('Flow has no settings.');
  }
  const match = settings.find((c) => c.name === options.flowName);
  if (!match) {
    throw new Error(
      `Flow "${options.flowName}" not found. Available: ${settings.map((c) => c.name).join(', ')}`,
    );
  }
  return match.id;
}

async function getAvailableFlowNames(options: {
  flowId: string;
  projectId: string;
}): Promise<string[]> {
  const flow = await getFlow({
    flowId: options.flowId,
    projectId: options.projectId,
  });
  const settings = (flow as { settings?: Array<{ name: string }> }).settings;
  return settings?.map((c) => c.name) ?? [];
}

/**
 * Default client-side wait budget for a streamed deploy, in milliseconds.
 *
 * The server's deploy budget is 10 minutes; this default exceeds it so a
 * slow-but-healthy long deploy is never aborted client-side and misread as a
 * failure in CI. `--wait <seconds>` (the `timeout` option) overrides it.
 */
export const DEFAULT_DEPLOY_WAIT_MS = 12 * 60 * 1000;

// === SSE Streaming ===

interface DeploymentResult {
  status: string;
  substatus?: string | null;
  type?: string;
  containerUrl?: string | null;
  publicUrl?: string | null;
  errorMessage?: string | null;
  [key: string]: unknown;
}

export async function streamDeploymentStatus(
  projectId: string,
  deploymentId: string,
  options: {
    timeout?: number;
    signal?: AbortSignal;
    onStatus?: (status: string, substatus: string | null) => void;
  },
): Promise<DeploymentResult> {
  const timeoutMs = options.timeout ?? DEFAULT_DEPLOY_WAIT_MS;

  const response = await apiFetch(
    `/api/projects/${projectId}/deployments/${deploymentId}/stream`,
    {
      headers: { Accept: 'text/event-stream' },
      signal: options.signal ?? AbortSignal.timeout(timeoutMs),
    },
  );

  if (!response.ok) throw new Error(`Stream failed: ${response.status}`);
  if (!response.body) throw new Error('No response body');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let result: DeploymentResult | null = null;
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const { parsed, remainder } = parseSSEEvents(buffer);
      buffer = remainder;

      for (const event of parsed) {
        if (event.type === 'status') {
          const data = JSON.parse(event.data);
          result = data;
          options.onStatus?.(data.status, data.substatus ?? null);
        }
        if (event.type === 'done') {
          return result!;
        }
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }

  if (!result) throw new Error('Stream ended without terminal status');
  return result;
}

// === Programmatic API ===

export interface DeployOptions {
  flowId: string;
  projectId?: string;
  wait?: boolean;
  flowName?: string;
  timeout?: number; // ms, default 120_000
  signal?: AbortSignal;
  onStatus?: (status: string, substatus: string | null) => void;
}

export async function deploy(options: DeployOptions) {
  const projectId = options.projectId ?? requireProjectId();
  const client = createApiClient();

  if (options.flowName) {
    const settingsId = await resolveSettingsId({
      flowId: options.flowId,
      projectId,
      flowName: options.flowName,
    });
    return deploySettings({
      ...options,
      projectId,
      settingsId,
      timeout: options.timeout,
      signal: options.signal,
      onStatus: options.onStatus,
    });
  }

  // Legacy path. A fresh Idempotency-Key per invocation means a retry after a
  // failed attempt is a new deploy, not a silent replay of `already_created`.
  const { data, error } = await client.POST(
    '/api/projects/{projectId}/flows/{flowId}/deploy',
    {
      params: { path: { projectId, flowId: options.flowId } },
      headers: { 'Idempotency-Key': randomUUID() },
    },
  );

  if (error) {
    try {
      throwApiError(error, 'Failed to start deployment');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'AMBIGUOUS_CONFIG') {
        const names = await getAvailableFlowNames({
          flowId: options.flowId,
          projectId,
        });
        throw new ApiError(
          `This flow has multiple settings. Use --flow <name> to specify one.\nAvailable: ${names.join(', ')}`,
          { code: 'AMBIGUOUS_CONFIG' },
        );
      }
      throw e;
    }
  }

  if (!options.wait) return data;

  // Stream deployment status via SSE
  const result = await streamDeploymentStatus(projectId, data.deploymentId, {
    timeout: options.timeout,
    signal: options.signal,
    onStatus: options.onStatus,
  });

  return { ...data, ...result };
}

// The per-settings deploy route now has an OpenAPI contract, so the success
// body is typed (`DeploySettingsResponse`). It still uses a raw `apiFetch`
// rather than the typed `client.POST()` on purpose: the bounded Retry-After
// retry below inspects `response.status` and the `Retry-After` header, and the
// `--wait` path then streams `text/event-stream`. openapi-fetch's `{ data,
// error }` client hides the raw `Response`, so it cannot drive either path.
async function deploySettings(options: {
  flowId: string;
  projectId: string;
  settingsId: string;
  wait?: boolean;
  timeout?: number;
  signal?: AbortSignal;
  onStatus?: (status: string, substatus: string | null) => void;
}) {
  const { flowId, projectId, settingsId } = options;

  // 1. Trigger per-settings deploy. A fresh Idempotency-Key per invocation
  // means a retry after a failed attempt is a new deploy, not a silent replay
  // of `already_created`.
  const triggerDeploy = () =>
    apiFetch(
      `/api/projects/${projectId}/flows/${flowId}/settings/${settingsId}/deploy`,
      { method: 'POST', headers: { 'Idempotency-Key': randomUUID() } },
    );

  let response = await triggerDeploy();

  // For `--wait`, honor a single bounded Retry-After on a rate-limited trigger
  // before giving up, so a brief deploy-rate ceiling does not fail an
  // otherwise healthy automated deploy.
  if (!response.ok && options.wait) {
    let retryBody: unknown = {};
    try {
      retryBody = await response.clone().json();
    } catch {
      retryBody = {};
    }
    try {
      throwApiResponseError(
        response,
        retryBody,
        `Deploy failed (${response.status})`,
      );
    } catch (e) {
      if (e instanceof ApiError && e.retryable) {
        const waitSeconds = Math.min(e.retryAfterSeconds ?? 5, 60);
        options.onStatus?.('rate_limited', `retrying in ${waitSeconds}s`);
        await new Promise((resolve) => setTimeout(resolve, waitSeconds * 1000));
        response = await triggerDeploy();
      } else {
        throw e;
      }
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throwApiResponseError(response, body, `Deploy failed (${response.status})`);
  }

  const data = (await response.json()) as DeploySettingsResponse;
  if (!options.wait) return data;

  // 2. Stream deployment status via SSE
  const result = await streamDeploymentStatus(projectId, data.deploymentId, {
    timeout: options.timeout,
    signal: options.signal,
    onStatus: options.onStatus,
  });

  return { ...data, ...result };
}

export async function getDeployment(options: {
  flowId: string;
  projectId?: string;
  flowName?: string;
}) {
  const projectId = options.projectId ?? requireProjectId();

  if (options.flowName) {
    const settingsId = await resolveSettingsId({
      flowId: options.flowId,
      projectId,
      flowName: options.flowName,
    });
    const response = await apiFetch(
      `/api/projects/${projectId}/flows/${options.flowId}/settings/${settingsId}/deploy`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throwApiResponseError(response, body, 'Failed to get deployment');
    }
    return response.json();
  }

  const client = createApiClient();

  const { data, error } = await client.GET(
    '/api/projects/{projectId}/flows/{flowId}/deploy',
    { params: { path: { projectId, flowId: options.flowId } } },
  );
  if (error) throwApiError(error, 'Failed to get deployment');
  return data;
}

// === CLI Commands ===

interface DeployCommandOptions extends GlobalOptions {
  project?: string;
  flow?: string;
  wait?: boolean;
  timeout?: string;
  output?: string;
  json?: boolean;
}

// Progress labels keyed on the statuses the deploy stream ACTUALLY emits. The
// server status enum is idle | deploying | published | active | stopped |
// failed; the bundle phase is reported as `deploying`/`building`, not a
// separate `bundling` status. Keys are `status` or `status:substatus`.
const statusLabels: Record<string, string> = {
  'deploying:building': 'Building bundle...',
  'deploying:publishing': 'Publishing to web...',
  'deploying:provisioning': 'Provisioning container...',
  'deploying:starting': 'Starting container...',
  deploying: 'Deploying...',
  active: 'Container is live',
  published: 'Published',
  stopped: 'Stopped',
  failed: 'Deployment failed',
};

/**
 * Map a streamed `(status, substatus)` to a human progress line, falling back
 * to the bare status label, then to a safe `Status: <raw>` line for an unknown
 * status so an unexpected value still renders something instead of nothing.
 */
export function renderStatusLabel(
  status: string,
  substatus: string | null,
): string {
  const key = substatus ? `${status}:${substatus}` : status;
  return statusLabels[key] || statusLabels[status] || `Status: ${status}`;
}

export async function deployCommand(
  flowId: string,
  options: DeployCommandOptions,
) {
  const log = createCLILogger(options);

  const timeoutMs = options.timeout
    ? parseInt(options.timeout, 10) * 1000
    : undefined;

  try {
    const result = await deploy({
      flowId,
      projectId: options.project,
      flowName: options.flow,
      wait: options.wait !== false,
      timeout: timeoutMs,
      onStatus: options.json
        ? undefined
        : (status, substatus) => {
            log.info(renderStatusLabel(status, substatus));
          },
    });

    if (options.json) {
      await writeResult(JSON.stringify(result, null, 2), options);
      return;
    }

    const r = result as Record<string, unknown>;

    if (r.status === 'published') {
      log.info(`Published: ${r.publicUrl}`);
    } else if (r.status === 'active') {
      log.info(`Active: ${r.containerUrl}`);
    } else if (r.status === 'failed') {
      log.error(`Failed: ${r.errorMessage || 'Unknown error'}`);
      process.exit(1);
    } else if (r.status === 'deploying') {
      // Non-wait path: the deploy-trigger response reports `deploying` (the
      // server status enum has no `bundling`; the build phase is a substatus).
      log.info(`Deployment started: ${r.deploymentId} (${r.type})`);
    } else {
      log.info(`Status: ${r.status}`);
    }
  } catch (err) {
    handleCliError(err);
  }
}

export async function getDeploymentCommand(
  flowId: string,
  options: DeployCommandOptions,
) {
  const log = createCLILogger(options);

  try {
    const result = await getDeployment({
      flowId,
      projectId: options.project,
      flowName: options.flow,
    });

    if (options.json) {
      await writeResult(JSON.stringify(result, null, 2), options);
      return;
    }

    if (!result) {
      log.info('No deployment found');
      return;
    }

    const r = result as Record<string, unknown>;
    log.info(`Deployment: ${r.id}`);
    log.info(`Type: ${r.type}`);
    log.info(`Status: ${r.status}`);
    if (r.containerUrl) log.info(`Endpoint: ${r.containerUrl}`);
    if (r.publicUrl) log.info(`URL: ${r.publicUrl}`);
    if (r.errorMessage) log.error(`Error: ${r.errorMessage}`);
  } catch (err) {
    handleCliError(err);
  }
}
